import { useState, useEffect } from 'react';
import { api } from '../api';
import { useTripBuilder } from '../TripBuilderContext';
import bookingLinks from '../bookingLinks';
import FlightInsightCard from './FlightInsightCard';

// Source name mapping for display
const SOURCE_NAMES = {
  aeroplan: 'Aeroplan',
  united: 'United',
  flyingblue: 'Flying Blue',
  lifemiles: 'LifeMiles',
  virginatlantic: 'Virgin Atlantic',
  american: 'AAdvantage',
  alaska: 'Alaska',
  delta: 'Delta SkyMiles',
  emirates: 'Emirates',
  ana: 'ANA',
  british_airways: 'BA Avios',
  iberia: 'Iberia Avios',
  qantas: 'Qantas',
  cathay: 'Cathay',
  singapore: 'Singapore KrisFlyer',
};

// Map seats.aero source slug → bookingLinks key
const SOURCE_TO_BOOKING_KEY = {
  aeroplan: 'Aeroplan',
  united: 'Aeroplan',
  flyingblue: 'Flying Blue',
  lifemiles: 'LifeMiles',
  virginatlantic: 'Virgin Atlantic',
  american: 'BA Avios',
  delta: 'Delta SkyMiles',
  emirates: 'Emirates Skywards',
  ana: 'ANA Mileage Club',
  british_airways: 'BA Avios',
  iberia: 'Iberia Avios',
};

function formatSource(s) {
  return SOURCE_NAMES[s] || s.charAt(0).toUpperCase() + s.slice(1);
}

function formatDate(dateStr) {
  const d = new Date(dateStr + 'T12:00:00');
  return d.toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' });
}

// `oneWay` — when true, only fetches and displays origin → destination
// (no Return tab, no swap, no destination → origin fetch). Used for every
// per-leg widget in a multi-city trip so each widget shows ONLY the actual
// direction of travel for that leg (the prior round-trip behavior would
// also show home → first-city under "Return", which is wrong for legs).
// `legIndex` — when provided, "Select This Flight" picks for this specific
// leg of a multi-city trip (so outbound/inter-city/return are tracked
// independently). When omitted, falls back to the legacy single-flight
// slot used by round-trip mode.
export default function LiveAvailability({ origin = 'JFK', origins, destinationCodes = [], cabin = 'business', oneWay = false, legIndex, onTripSelect, onDataLoaded }) {
  const [direction, setDirection] = useState('outbound');

  // Outbound state
  const [results, setResults] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [selectedDate, setSelectedDate] = useState(null);
  const [expandedTrip, setExpandedTrip] = useState(null);
  const [tripDetail, setTripDetail] = useState(null);
  const [tripLoading, setTripLoading] = useState(false);

  // Direct-only filter — toggle that hides connecting flights. Persisted
  // to localStorage so the preference sticks across sessions.
  const [directOnly, setDirectOnly] = useState(() => {
    try { return localStorage.getItem('pp_direct_only') === 'true'; } catch { return false; }
  });
  const toggleDirectOnly = () => {
    setDirectOnly(prev => {
      const next = !prev;
      try { localStorage.setItem('pp_direct_only', String(next)); } catch {}
      return next;
    });
  };

  // Return state
  const [returnResults, setReturnResults] = useState(null);
  const [returnLoading, setReturnLoading] = useState(false);
  const [returnError, setReturnError] = useState(null);
  const [returnSelectedDate, setReturnSelectedDate] = useState(null);
  const [returnExpandedTrip, setReturnExpandedTrip] = useState(null);
  const [returnTripDetail, setReturnTripDetail] = useState(null);
  const [returnTripLoading, setReturnTripLoading] = useState(false);

  const { pickFlightFromLive, pickFlightForLeg, flight, flightsByLeg } = useTripBuilder();
  // Per-leg mode picks into flightsByLeg[legIndex]; otherwise legacy slot.
  const pickFn = (typeof legIndex === 'number')
    ? (r, c) => pickFlightForLeg(legIndex, r, c)
    : pickFlightFromLive;
  const pickedSlot = (typeof legIndex === 'number') ? flightsByLeg[legIndex] : flight;

  const destination = destinationCodes[0] || 'LIS';

  // Multi-origin support — if `origins` array is provided (e.g.
  // [JFK, EWR, LGA] from a metro-area checkbox), fetch each in
  // parallel + merge results so user sees the cheapest across all
  // nearby airports.
  const originsList = (Array.isArray(origins) && origins.length > 0) ? origins : [origin];
  const originsKey = originsList.join(',');

  // Fetch outbound availability across all origin airports
  useEffect(() => {
    if (!destination) return;
    setLoading(true);
    setError(null);
    setResults(null);
    setSelectedDate(null);
    Promise.all(originsList.map(o =>
      api.searchLiveAvailability(o, destination, cabin).catch(() => ({ availableDates: [], byDate: {} }))
    ))
      .then(results => {
        // Merge byDate across origins
        const merged = { availableDates: [], byDate: {} };
        const datesSet = new Set();
        results.forEach(data => {
          (data.availableDates || []).forEach(d => datesSet.add(d));
          Object.entries(data.byDate || {}).forEach(([date, rows]) => {
            if (!merged.byDate[date]) merged.byDate[date] = [];
            merged.byDate[date].push(...rows);
          });
        });
        merged.availableDates = Array.from(datesSet).sort();
        setResults(merged);
        if (merged.availableDates.length) setSelectedDate(merged.availableDates[0]);
        if (onDataLoaded) onDataLoaded(merged.availableDates.length > 0);
      })
      .catch(err => {
        setError(err.message);
        if (onDataLoaded) onDataLoaded(false);
      })
      .finally(() => setLoading(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [originsKey, destination, cabin]);

  // Fetch return availability (destination → origin)
  // Skipped in one-way mode — used by multi-city per-leg widgets where the
  // "return" of a leg isn't meaningful (the return for the whole trip lives
  // on its own dedicated leg widget).
  useEffect(() => {
    if (!destination || oneWay) return;
    setReturnLoading(true);
    setReturnError(null);
    setReturnResults(null);
    setReturnSelectedDate(null);
    api.searchLiveAvailability(destination, origin, cabin)
      .then(data => {
        setReturnResults(data);
        if (data.availableDates?.length) setReturnSelectedDate(data.availableDates[0]);
      })
      .catch(err => setReturnError(err.message))
      .finally(() => setReturnLoading(false));
  }, [origin, destination, cabin, oneWay]);

  // In one-way mode, force the displayed direction to outbound so toggling
  // state (which we don't render) can never accidentally show stale return.
  useEffect(() => {
    if (oneWay && direction !== 'outbound') setDirection('outbound');
  }, [oneWay, direction]);

  // Active state based on direction
  const activeResults = direction === 'outbound' ? results : returnResults;
  const activeLoading = direction === 'outbound' ? loading : returnLoading;
  const activeError = direction === 'outbound' ? error : returnError;
  const activeSelectedDate = direction === 'outbound' ? selectedDate : returnSelectedDate;
  const setActiveSelectedDate = direction === 'outbound' ? setSelectedDate : setReturnSelectedDate;
  const activeExpandedTrip = direction === 'outbound' ? expandedTrip : returnExpandedTrip;
  const activeTripDetail = direction === 'outbound' ? tripDetail : returnTripDetail;
  const activeTripLoading = direction === 'outbound' ? tripLoading : returnTripLoading;

  const activeOrigin = direction === 'outbound' ? origin : destination;
  const activeDestination = direction === 'outbound' ? destination : origin;

  async function loadTripDetail(tripId) {
    const currentExpanded = direction === 'outbound' ? expandedTrip : returnExpandedTrip;
    const setExpanded = direction === 'outbound' ? setExpandedTrip : setReturnExpandedTrip;
    const setDetail = direction === 'outbound' ? setTripDetail : setReturnTripDetail;
    const setDetailLoading = direction === 'outbound' ? setTripLoading : setReturnTripLoading;

    if (currentExpanded === tripId) { setExpanded(null); return; }
    setExpanded(tripId);
    setDetailLoading(true);
    try {
      const data = await api.getLiveTrip(tripId);
      setDetail(data);
    } catch (e) {
      setDetail(null);
    }
    setDetailLoading(false);
  }

  // Summary counts
  const outboundDates = results?.availableDates?.length || 0;
  const returnDates = returnResults?.availableDates?.length || 0;
  const showSummary = !loading && !returnLoading && (outboundDates > 0 || returnDates > 0);

  if (activeLoading) {
    return (
      <div className="bg-navy-light border border-brand-ink/10 rounded-xl p-6">
        <div className="flex items-center gap-3">
          <div className="w-3 h-3 rounded-full bg-teal animate-pulse" />
          <div>
            <div className="font-semibold text-sm">Searching live award seats...</div>
            <div className="text-xs text-brand-ink/65">Checking {activeOrigin} → {activeDestination} via seats.aero</div>
          </div>
        </div>
      </div>
    );
  }

  if (activeError) {
    return (
      <div className="bg-navy-light border border-brand-ink/10 rounded-xl p-4">
        <div className="text-xs text-brand-ink/65">Live availability unavailable — showing saved options below</div>
      </div>
    );
  }

  if (!activeResults || !activeResults.availableDates?.length) {
    if (activeResults && activeResults.count === 0) {
      return (
        <div className="bg-navy-light border border-amber-500/20 rounded-xl p-4">
          {/* Direction toggle — hidden in one-way (per-leg) mode */}
          {!oneWay && <DirectionToggle direction={direction} setDirection={setDirection} origin={origin} destination={destination} />}
          <div className="text-sm font-medium text-amber-400 mt-2">No award seats found {activeOrigin}→{activeDestination}</div>
          <div className="text-xs text-brand-ink/65 mt-1">
            seats.aero doesn't have business class cached for this route + month right now. This direction often has tighter award availability than the outbound — three things to try:
          </div>
          <ul className="text-xs text-brand-ink/75 mt-2 space-y-1 list-disc pl-4">
            <li>Check directly on <a className="text-teal underline" href={`https://seats.aero/search?origin_airport=${activeOrigin}&destination_airport=${activeDestination}&cabin=business`} target="_blank" rel="noopener noreferrer">seats.aero for {activeOrigin}→{activeDestination}</a></li>
            <li>Try Avios / Iberia / Flying Blue / Virgin Atlantic search engines directly — they sometimes show seats seats.aero hasn't cached</li>
            <li>Widen the date window — being flexible by ±3 days unlocks 2-3x the inventory on most European routes</li>
          </ul>
          {showSummary && <SummaryBar outboundDates={outboundDates} returnDates={returnDates} />}
        </div>
      );
    }
    return null;
  }

  const dateResults = activeSelectedDate ? (activeResults.byDate[activeSelectedDate] || []) : [];
  const availableAll = dateResults.filter(r => r.businessAvailable || r.firstAvailable);
  // Apply direct-only filter if toggled. If filtering would leave 0,
  // fall through to showing all with a flag so user isn't blocked.
  const available = directOnly
    ? (availableAll.filter(r => r.businessDirect || r.firstDirect || r.direct).length > 0
       ? availableAll.filter(r => r.businessDirect || r.firstDirect || r.direct)
       : availableAll)
    : availableAll;
  const directHidden = directOnly ? availableAll.length - available.length : 0;

  return (
    <div className="bg-gradient-to-br from-teal/5 to-navy-light border border-teal/20 rounded-xl overflow-hidden">
      {/* Header */}
      <div className="px-4 pt-4 pb-2">
        <div className="flex items-center gap-2">
          <div className="w-2 h-2 rounded-full bg-green-400 animate-pulse" />
          <span className="text-xs font-bold text-teal uppercase tracking-wider">Live Award Seats</span>
          <span className="text-[10px] text-brand-ink/55 ml-auto">via seats.aero</span>
        </div>
      </div>

      {/* Direction Toggle */}
      <div className="px-4 pb-2">
        {!oneWay && <DirectionToggle direction={direction} setDirection={setDirection} origin={origin} destination={destination} />}
        <div className="text-sm text-brand-ink/75 mt-2">
          {activeResults.availableDates.length} dates with {cabin} class availability in Sep 2026
        </div>
      </div>

      {/* Date Scroller */}
      <div className="px-4 pb-3">
        <div className="flex gap-1.5 overflow-x-auto pb-2 scrollbar-hide">
          {activeResults.availableDates.map(date => {
            const dayResults = activeResults.byDate[date] || [];
            const hasJ = dayResults.some(r => r.businessAvailable);
            const hasF = dayResults.some(r => r.firstAvailable);
            const isSelected = date === activeSelectedDate;
            return (
              <button
                key={date}
                onClick={() => setActiveSelectedDate(date)}
                className={`flex-shrink-0 px-3 py-2 rounded-lg text-xs font-medium transition-all ${
                  isSelected
                    ? 'bg-teal text-navy'
                    : 'bg-brand-ink/5 text-brand-ink/75 hover:bg-brand-ink/10'
                }`}
              >
                <div>{formatDate(date)}</div>
                <div className="flex gap-1 mt-0.5 justify-center">
                  {hasJ && <span className={`text-[9px] font-bold ${isSelected ? 'text-navy/60' : 'text-indigo-300'}`}>J</span>}
                  {hasF && <span className={`text-[9px] font-bold ${isSelected ? 'text-navy/60' : 'text-gold'}`}>F</span>}
                </div>
              </button>
            );
          })}
        </div>
      </div>

      {/* Direct-only filter toggle — persists in localStorage */}
      <div className="px-4 pt-2 flex items-center justify-end gap-2 text-[11px]">
        <span className="text-brand-ink/65">Direct flights only</span>
        <button
          onClick={toggleDirectOnly}
          aria-label="Toggle direct-only filter"
          className={`relative w-10 h-5 rounded-full transition shrink-0 ${directOnly ? 'bg-teal' : 'bg-brand-ink/20'}`}
        >
          <div className={`absolute top-0.5 w-4 h-4 rounded-full bg-white shadow transition ${directOnly ? 'left-[22px]' : 'left-0.5'}`} />
        </button>
      </div>
      {directHidden > 0 && (
        <div className="px-4 text-[10px] text-amber-300">
          Hiding {directHidden} connecting option{directHidden === 1 ? '' : 's'} (direct-only filter active)
        </div>
      )}
      {directOnly && available.length > 0 && availableAll.filter(r => r.businessDirect || r.firstDirect || r.direct).length === 0 && (
        <div className="px-4 text-[10px] text-amber-300">
          ⚠ No direct flights found — showing connections anyway. Try different dates for nonstops.
        </div>
      )}

      {/* Results for Selected Date */}
      {activeSelectedDate && (
        <div className="px-4 pb-4 space-y-2">
          <div className="text-xs text-brand-ink/65 font-medium">{formatDate(activeSelectedDate)} — {available.length} option{available.length !== 1 ? 's' : ''}</div>
          {available.map((r, i) => {
            const isSelectedForBudget = pickedSlot?.id === `live-${r.id || ''}`;
            return (
              <div key={r.id || i} className={`bg-brand-ink/5 rounded-lg p-3 space-y-2 ${isSelectedForBudget ? 'ring-1 ring-teal/40' : ''}`}>
                <div className="flex items-center justify-between">
                  <div className="space-y-0.5">
                    <div className="flex items-center gap-2">
                      <span className="font-semibold text-sm">{formatSource(r.source)}</span>
                      {r.airlines && <span className="text-xs text-brand-ink/65">on {r.airlines}</span>}
                    </div>
                    <div className="flex items-center gap-2 text-xs text-brand-ink/70">
                      <span>{r.route?.OriginAirport} → {r.route?.DestinationAirport}</span>
                    </div>
                  </div>
                  <div className="text-right">
                    {r.businessAvailable && (
                      <div>
                        <span className="text-lg font-black">{r.businessMiles}</span>
                        <span className="text-[10px] text-brand-ink/65 ml-1">pts/person</span>
                      </div>
                    )}
                    {r.firstAvailable && !r.businessAvailable && (
                      <div>
                        <span className="text-lg font-black text-gold">{r.firstMiles}</span>
                        <span className="text-[10px] text-brand-ink/65 ml-1">First</span>
                      </div>
                    )}
                  </div>
                </div>

                <div className="flex items-center gap-3 text-[10px]">
                  {r.businessAvailable && (
                    <span className="flex items-center gap-1">
                      <span className="w-1.5 h-1.5 rounded-full bg-indigo-400" />
                      <span className="text-indigo-300">Business: {r.businessSeats > 0 ? `${r.businessSeats} seat${r.businessSeats !== 1 ? 's' : ''}` : 'available'}</span>
                      {r.businessTaxes > 0 && <span className="text-brand-ink/55">+ {r.taxesCurrency === 'USD' ? '$' : ''}{r.businessTaxes} tax</span>}
                    </span>
                  )}
                  {r.firstAvailable && (
                    <span className="flex items-center gap-1">
                      <span className="w-1.5 h-1.5 rounded-full bg-gold" />
                      <span className="text-gold">First: {r.firstSeats > 0 ? `${r.firstSeats} seat${r.firstSeats !== 1 ? 's' : ''}` : 'available'}</span>
                      {r.firstTaxes > 0 && <span className="text-brand-ink/55">+ {r.taxesCurrency === 'USD' ? '$' : ''}{r.firstTaxes} tax</span>}
                    </span>
                  )}
                </div>

                {r.businessAvailable && r.firstAvailable && (
                  <div className="text-[10px] text-gold/60">
                    First class also available: {r.firstMiles} pts/person
                  </div>
                )}

                <div className="flex flex-wrap gap-2 mt-2">
                  {/* ALWAYS show flight-times button — the user explicitly
                      asked for flight times to be visible. If we have an ID,
                      lazy-load segment detail; if not, fall through to the
                      seats.aero deep link so they can see times externally. */}
                  {r.id ? (
                    <button
                      onClick={() => loadTripDetail(r.id)}
                      className="flex-1 min-w-[110px] py-2 rounded-lg text-sm font-bold bg-gold/15 text-gold border border-gold/40 hover:bg-gold/25 transition"
                    >
                      {activeExpandedTrip === r.id ? '▲ Hide times' : '🛫 Show flight times + aircraft'}
                    </button>
                  ) : (
                    <a
                      href={`https://seats.aero/search?origin=${activeOrigin}&destination=${activeDestination}&cabin=business`}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="flex-1 min-w-[110px] py-2 rounded-lg text-sm font-bold bg-gold/15 text-gold border border-gold/40 hover:bg-gold/25 transition text-center"
                    >
                      🛫 See flight times on seats.aero ↗
                    </a>
                  )}
                  {bookingLinks[SOURCE_TO_BOOKING_KEY[r.source]] && (
                    <a
                      href={bookingLinks[SOURCE_TO_BOOKING_KEY[r.source]].search}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="flex-1 min-w-[110px] py-2 rounded-lg text-sm font-medium bg-indigo-600/80 text-brand-cream hover:bg-indigo-600 transition text-center"
                    >
                      Book on {formatSource(r.source)} ↗
                    </a>
                  )}
                  {/* Allow selecting from EITHER tab so user is never blocked.
                      Round-trip awards are priced as RT either way — picking
                      from Return tab is equivalent to picking from Outbound
                      since the airline books both directions on one redemption. */}
                  <button
                    onClick={() => pickFn(r, r.businessAvailable ? 'business' : 'first')}
                    className={`flex-1 min-w-[110px] py-2 rounded-lg text-sm font-bold transition ${
                      isSelectedForBudget
                        ? 'bg-teal/20 text-teal'
                        : 'bg-teal text-navy hover:bg-teal-dark'
                    }`}
                  >
                    {isSelectedForBudget ? '✓ Selected — keep moving' : (direction === 'return' && !oneWay ? '✓ Confirm return + select trip' : '→ Select this flight')}
                  </button>
                  <span className="basis-full text-[10px] text-brand-ink/45 text-right">Updated {new Date(r.updatedAt).toLocaleDateString()}</span>
                </div>

                {/* Per-flight insight: program critique, business class
                    product notes, and step-by-step conversion guide showing
                    every source currency that can transfer to this program. */}
                <FlightInsightCard source={r.source} />

                {/* Trip Detail Expansion */}
                {activeExpandedTrip === r.id && (
                  <div className="bg-brand-ink/5 rounded-lg p-3 mt-1">
                    {activeTripLoading ? (
                      <div className="text-xs text-brand-ink/65 animate-pulse">Loading flight details...</div>
                    ) : activeTripDetail?.data ? (
                      <div className="space-y-3">
                        {activeTripDetail.data.map((trip, ti) => (
                          <div key={ti} className="space-y-1.5">
                            <div className="flex items-center justify-between text-xs">
                              <span className="font-medium">{trip.Carriers} — {trip.Cabin}</span>
                              <span className="text-brand-ink/65">{trip.Stops === 0 ? 'Nonstop' : `${trip.Stops} stop${trip.Stops > 1 ? 's' : ''}`} · {Math.floor(trip.TotalDuration / 60)}h {trip.TotalDuration % 60}m</span>
                            </div>
                            <div className="text-xs text-brand-ink/70">
                              {trip.MileageCost?.toLocaleString()} pts + {trip.TaxesCurrencySymbol || '$'}{Math.round((trip.TotalTaxes || 0) / 100)} taxes
                              {trip.RemainingSeats > 0 && <span className="text-teal ml-2">({trip.RemainingSeats} seats left)</span>}
                            </div>
                            {trip.AvailabilitySegments?.map((seg, si) => (
                              <div key={si} className="text-[10px] text-brand-ink/65 pl-3 border-l border-brand-ink/10">
                                {seg.FlightNumber} · {seg.AircraftName || seg.AircraftCode} · {seg.OriginAirport}→{seg.DestinationAirport}
                                {seg.DepartsAt && ` · ${new Date(seg.DepartsAt).toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit' })}`}
                              </div>
                            ))}
                          </div>
                        ))}
                        {/* Booking Links */}
                        {activeTripDetail.booking_links?.length > 0 && (
                          <div className="pt-2 border-t border-brand-ink/10 flex flex-wrap gap-2">
                            {activeTripDetail.booking_links.map((bl, bi) => (
                              <a
                                key={bi}
                                href={bl.link}
                                target="_blank"
                                rel="noopener noreferrer"
                                className={`text-[11px] px-3 py-1.5 rounded-lg font-medium transition ${
                                  bl.primary ? 'bg-teal text-navy hover:bg-teal-dark' : 'bg-brand-ink/10 text-brand-ink/75 hover:bg-white/15'
                                }`}
                              >
                                {bl.label || 'Book'}
                              </a>
                            ))}
                          </div>
                        )}
                      </div>
                    ) : (
                      <div className="text-xs text-brand-ink/65">Could not load trip details</div>
                    )}
                  </div>
                )}
              </div>
            );
          })}
          {available.length === 0 && dateResults.length > 0 && (
            <div className="text-xs text-brand-ink/65 text-center py-2">No {cabin} class seats found for this date</div>
          )}
        </div>
      )}

      {/* Summary */}
      {showSummary && (
        <div className="px-4 pb-4">
          <SummaryBar outboundDates={outboundDates} returnDates={returnDates} />
        </div>
      )}
    </div>
  );
}

function DirectionToggle({ direction, setDirection, origin, destination }) {
  return (
    <div className="flex gap-1 bg-brand-ink/5 rounded-lg p-0.5">
      <button
        onClick={() => setDirection('outbound')}
        className={`flex-1 px-3 py-1.5 rounded-md text-xs font-medium transition ${
          direction === 'outbound' ? 'bg-teal text-navy' : 'text-brand-ink/70 hover:text-brand-ink/80'
        }`}
      >
        Outbound · {origin} → {destination}
      </button>
      <button
        onClick={() => setDirection('return')}
        className={`flex-1 px-3 py-1.5 rounded-md text-xs font-medium transition ${
          direction === 'return' ? 'bg-teal text-navy' : 'text-brand-ink/70 hover:text-brand-ink/80'
        }`}
      >
        Return · {destination} → {origin}
      </button>
    </div>
  );
}

function SummaryBar({ outboundDates, returnDates }) {
  return (
    <div className="flex items-center gap-3 bg-brand-ink/5 rounded-lg px-3 py-2 text-[11px] text-brand-ink/70">
      <span className={outboundDates > 0 ? 'text-teal' : 'text-brand-ink/55'}>
        {outboundDates > 0 ? '\u2713' : '\u2013'} Outbound seats found on {outboundDates} date{outboundDates !== 1 ? 's' : ''}
      </span>
      <span className="text-brand-ink/45">·</span>
      <span className={returnDates > 0 ? 'text-teal' : 'text-brand-ink/55'}>
        {returnDates > 0 ? '\u2713' : '\u2013'} Return seats found on {returnDates} date{returnDates !== 1 ? 's' : ''}
      </span>
    </div>
  );
}
